import React from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Formik, Form, Field, FieldProps } from 'formik';
import { Route, Redirect } from 'react-router-dom';
import { Mutation } from '@apollo/client/react/components';

import Helmet from 'helmet/helmet';
import SVGIcon from 'lib/images/svg-pure-icon/svg-pure-icon';

import { Site } from 'four-nets/site';
import Input from 'four-nets/ui-v2/form/input/input';
import deleteIcon from 'four-nets/icons/svg/icon_delete.svg';
import saveIcon from 'four-nets/icons/svg/icon_save.svg';
import { mergeValidators, emptyValidator } from 'four-nets/ui-v2/form/validators';
import { AlertContext } from 'lib/alert-context/alert-context';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';

import mutation, {
    UpdateCollectionMutation,
    UpdateCollectionMutationVariables,
} from './mutations/update-collection.graphql';
import removeMutation, {
    RemoveCollectionMutation,
    RemoveCollectionMutationVariables,
} from './mutations/remove-collection.graphql';
import { CollectionFragment } from '../collection/collection.graphql';
import CollectionInfo from '../collection/collection-info/collection-info';
import CollectionEditImage from './collection-edit-image/collection-edit-image';
import defaultStyles from './collection-edit.scss';

export interface Props extends CollectionFragment {
    styles: typeof defaultStyles;
}

interface State {
    selectedImages: Set<string>;
}

interface Values {
    title: string;
}
type Errors = Partial<{ [key in keyof Values]: Array<React.ReactElement<FormattedMessage>> | true }>;

class CollectionEdit extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    state: State = { selectedImages: new Set() };

    renderHelmet = (H: any) => {
        const { site } = this.context;
        const { title } = this.props;

        return (
            <FormattedMessage id='COLLECTIONS'>
                {message =>
                    title ? (
                        <H>
                            <title>
                                {message} - {title} | {site.name}
                            </title>
                        </H>
                    ) : (
                        <H>
                            <title>
                                {message} | {site.name}
                            </title>
                        </H>
                    )
                }
            </FormattedMessage>
        );
    };

    splitIntoCollumns = (array: any[] | null, columnCount: number = 4) => {
        if (!array) {
            return null;
        }

        const result: any[][] = new Array(columnCount);
        for (let i = 0; i < columnCount; i++) {
            result[i] = [];
        }
        let current = 0;

        array.forEach(element => {
            result[current].push(element);
            current = (current + 1) % columnCount;
        });
        return result;
    };

    onClick = (id: string) => {
        this.setState(prevState => {
            const newSelected = new Set(prevState.selectedImages);
            if (newSelected.has(id)) {
                newSelected.delete(id);
            } else {
                newSelected.add(id);
            }
            return { selectedImages: newSelected };
        });
    };

    mapCollections = (columnEdges, cIndex) => (
        <div className={this.props.styles.column} key={cIndex}>
            {columnEdges.map(edge =>
                edge && edge.node ? (
                    <CollectionEditImage
                        {...edge.node}
                        selected={this.state.selectedImages.has(edge.node.id)}
                        onClick={this.onClick}
                        key={edge.node.id}
                    />
                ) : null
            )}
        </div>
    );

    render() {
        const breadcrumbs = {
            to: ['/', '/blogs', '/blogs/collections', ''],
            labels: [
                <FormattedMessage id='HOMEPAGE' />,
                <FormattedMessage id='PHOTOBLOGS' />,
                <FormattedMessage id='COLLECTIONS' />,
                this.props.title,
            ],
        };

        const { styles, ...collection } = this.props;
        const collumns = this.splitIntoCollumns(
            collection.images && collection.images.edges && collection.images.edges
        );
        const collumnsLarge = this.splitIntoCollumns(
            collection.images && collection.images.edges && collection.images.edges
        );
        const collumnsMobile = this.splitIntoCollumns(
            collection.images && collection.images.edges && collection.images.edges,
            2
        );

        const RemoveButton = (
            <Mutation<RemoveCollectionMutation, RemoveCollectionMutationVariables> mutation={removeMutation}>
                {removeFn => (
                    <Route>
                        {({ history }) => (
                            <AlertContext.Consumer>
                                {({ confirm, retry }) => {
                                    const onAccept = () => {
                                        removeFn({ variables: { input: { id: collection.id } } })
                                            .then(() => history.push('/blogs/collections/'))
                                            .catch((err: Error) =>
                                                retry({
                                                    body: err.message,
                                                    onAccept,
                                                })
                                            );
                                    };
                                    return (
                                        <button
                                            type='button'
                                            className={styles.deleteButton}
                                            onClick={() =>
                                                confirm({
                                                    acceptButtonLabel: (
                                                        <FormattedMessage
                                                            id='DELETE_COLLECTION'
                                                            values={{ gender: 'v' }}
                                                        />
                                                    ),
                                                    body: (
                                                        <FormattedMessage
                                                            id='ARE_YOU_SURE_YOU_WANT_TO_DELETE_COLLECTION'
                                                            values={{ gender: 'v' }}
                                                        />
                                                    ),
                                                    onAccept,
                                                    acceptButtonType: 'warn',
                                                })
                                            }
                                        >
                                            <SVGIcon src={deleteIcon} className={styles.deleteIcon} />
                                            <FormattedMessage id='DELETE_COLLECTION' values={{ gender: 'v' }}>
                                                {msg => <span className={styles.deleteText}>{msg}</span>}
                                            </FormattedMessage>
                                        </button>
                                    );
                                }}
                            </AlertContext.Consumer>
                        )}
                    </Route>
                )}
            </Mutation>
        );

        return (
            <React.Fragment>
                <ViewerConsumer>
                    {({ viewer }) => {
                        if (collection.photoblog.user) {
                            const username = collection.photoblog.user.username;
                            const redirect = <Redirect to={`/blog/${username}/collection/${collection.slug}/`} />;

                            const isAuthor = viewer && username === viewer.username;
                            const isSuperstaff = viewer && viewer.isSuperstaff;

                            if (!isAuthor && !isSuperstaff) {
                                return redirect;
                            }
                        }
                        return null;
                    }}
                </ViewerConsumer>
                <Route>
                    {({ history }) => (
                        <Mutation<UpdateCollectionMutation, UpdateCollectionMutationVariables> mutation={mutation}>
                            {updateCollection => (
                                <Formik<Values>
                                    validate={values => {
                                        const errors: Errors = {};
                                        const title = mergeValidators([emptyValidator])(values.title);
                                        if (title) {
                                            errors.title = title;
                                        }
                                        return errors;
                                    }}
                                    initialValues={{ title: collection.title }}
                                    onSubmit={(values, actions) => {
                                        actions.setSubmitting(true);

                                        const username =
                                            collection.photoblog.user && collection.photoblog.user.username;

                                        updateCollection({
                                            variables: {
                                                input: {
                                                    id: collection.id,
                                                    title: values.title,
                                                    imagesToRemove: [...this.state.selectedImages.values()],
                                                },
                                            },
                                        })
                                            .then(() => {
                                                history.push(`/blog/${username}/collection/${collection.slug}/`);
                                            })
                                            .finally(() => {
                                                actions.setSubmitting(false);
                                            });
                                    }}
                                    render={({ isSubmitting, submitForm, touched, errors }) => (
                                        <Form>
                                            <Helmet render={this.renderHelmet} />
                                            <LoadingWrapperView loading={isSubmitting}>
                                                <div className={styles.row}>
                                                    <Field name='title'>
                                                        {({ field }: FieldProps) => (
                                                            <label className={styles.title}>
                                                                <FormattedMessage id='COLLECTION_TITLE'>
                                                                    {msg => (
                                                                        <span className={styles.titleName}>{msg}</span>
                                                                    )}
                                                                </FormattedMessage>
                                                                <div className={styles.rowTop}>
                                                                    <Input
                                                                        style={{
                                                                            wrapper: styles.wrapper,
                                                                            textInput: styles.textInput,
                                                                            input: styles.input,
                                                                        }}
                                                                        errors={
                                                                            errors.title ? [errors.title] : undefined
                                                                        }
                                                                        touched={touched.title}
                                                                        {...field}
                                                                        disabled={isSubmitting}
                                                                    />
                                                                    <div className={styles.save}>
                                                                        <button
                                                                            onClick={submitForm}
                                                                            disabled={isSubmitting}
                                                                            className={styles.submitButton}
                                                                            type='button'
                                                                        >
                                                                            <SVGIcon
                                                                                src={saveIcon}
                                                                                className={styles.submitIcon}
                                                                            />
                                                                            <FormattedMessage
                                                                                id='SAVE_CHANGES'
                                                                                values={{ gender: 'v' }}
                                                                            >
                                                                                {msg => (
                                                                                    <span className={styles.submitText}>
                                                                                        {msg}
                                                                                    </span>
                                                                                )}
                                                                            </FormattedMessage>
                                                                        </button>
                                                                        {RemoveButton}
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        )}
                                                    </Field>
                                                </div>
                                                <CollectionInfo {...collection} />
                                                <React.Fragment>
                                                    <div className={styles.masonryLarge}>
                                                        {collumns && collumns.map(this.mapCollections)}
                                                    </div>
                                                    <div className={styles.masonryMobile}>
                                                        {collumnsMobile && collumnsMobile.map(this.mapCollections)}
                                                    </div>
                                                </React.Fragment>
                                            </LoadingWrapperView>
                                        </Form>
                                    )}
                                />
                            )}
                        </Mutation>
                    )}
                </Route>
            </React.Fragment>
        );
    }
}

export default Styled(defaultStyles)(CollectionEdit);
